import React, { useState, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { AuthContext } from '../AuthContext';
import ReactMarkdown from 'react-markdown';
import './WhatIsNew.css';

// Maps internal link paths to navigation component keys.
// e.g. /nav/videos -> setActiveComponent('videos')
const NAV_PREFIX = '/nav/';

const WhatIsNew = ({ isPro = false, isAdmin: isAdminProp = false, setActiveComponent }) => {
    const [updates, setUpdates] = useState([]);
    const [newDate, setNewDate] = useState('');
    const [newUpdate, setNewUpdate] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [isPreview, setIsPreview] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editDate, setEditDate] = useState('');
    const [editUpdate, setEditUpdate] = useState('');
    const [isEditPreview, setIsEditPreview] = useState(false);
    const [galleries, setGalleries] = useState([]);
    const { isAdmin: isAdminCtx } = useContext(AuthContext);
    const isAdmin = isAdminProp || isAdminCtx;
    const canNavigate = isPro || isAdmin;

    const linkRenderer = {
        a({ href, children }) {
            if (href && href.startsWith(NAV_PREFIX) && canNavigate && setActiveComponent) {
                const componentKey = href.slice(NAV_PREFIX.length);
                return (
                    <button
                        className="whats-new-nav-link"
                        onClick={() => setActiveComponent(componentKey)}
                    >
                        {children}
                    </button>
                );
            }
            if (href && href.startsWith(NAV_PREFIX) && !canNavigate) {
                // Basic user: show label only, no link
                return <span className="whats-new-locked-link">{children}</span>;
            }
            // External links pass through normally
            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
        }
    };

    useEffect(() => {
        // Query updates ordered by date in descending order
        const q = query(collection(db, 'whats_new'), orderBy('timestamp', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updatesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUpdates(updatesData);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        getDocs(collection(db, 'gallery_config')).then(snapshot => {
            const data = snapshot.docs.map(d => ({
                name: d.data().name,
                collectionName: d.data().collectionName
            }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setGalleries(data);
        });
    }, [isAdmin]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            await addDoc(collection(db, 'whats_new'), {
                date: newDate,
                update: newUpdate,
                timestamp: new Date().toISOString() // For ordering
            });

            // Reset form
            setNewDate('');
            setNewUpdate('');
            setIsAdding(false);
            setIsPreview(false);
        } catch (error) {
            console.error("Error adding update:", error);
        }
    };

    const handleEdit = (update) => {
        setEditingId(update.id);
        setEditDate(update.date);
        setEditUpdate(update.update);
        setIsEditPreview(false);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        try {
            await updateDoc(doc(db, 'whats_new', editingId), {
                date: editDate,
                update: editUpdate,
                lastEdited: new Date().toISOString()
            });

            // Reset edit form
            setEditingId(null);
            setEditDate('');
            setEditUpdate('');
            setIsEditPreview(false);
        } catch (error) {
            console.error("Error updating post:", error);
        }
    };

    const handleDelete = async (updateId) => {
        if (window.confirm('Are you sure you want to delete this update?')) {
            try {
                await deleteDoc(doc(db, 'whats_new', updateId));
            } catch (error) {
                console.error("Error deleting post:", error);
            }
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDate('');
        setEditUpdate('');
        setIsEditPreview(false);
    };

    const galleryLinks = galleries.length
        ? galleries.map(g => `- ${g.name}: \`[${g.name}](/nav/${g.collectionName})\``).join('\n')
        : '- *(no galleries found)*';

    const markdownGuide = `
### Markdown Guide:
- **Bold**: \`**text**\`
- *Italic*: \`*text*\`
- # Heading 1: \`# text\`
- ## Heading 2: \`## text\`
- ### Heading 3: \`### text\`
- List item: \`- item\`
- [Link](url): \`[text](url)\`
- \`Code\`: \`\`\`code\`\`\`

### Nav Links (Pro users only):
- Videos: \`[Videos](/nav/videos)\`
- Pro Galleries: \`[Pro Galleries](/nav/pro_galleries)\`
- Pro Chat: \`[Pro Chat](/nav/pro_chat)\`
- Tech: \`[Tech](/nav/tech)\`
- Custom Build: \`[Custom Build](/nav/custom_build)\`

### Your Galleries:
${galleryLinks}

*Basic users will see nav link labels as plain text.*
    `;

    return (
        <div className="whats-new-container">
            <h1>Latest Updates</h1>
            <p>See what's new on Dannytopia!</p>
            
            {isAdmin && (
                <div className="admin-controls">
                    {!isAdding ? (
                        <button 
                            className="add-update-btn"
                            onClick={() => setIsAdding(true)}
                        >
                            Add New Update
                        </button>
                    ) : (
                        <form onSubmit={handleSubmit} className="add-update-form">
                            <input
                                type="text"
                                placeholder="Date (e.g., March 2024)"
                                value={newDate}
                                onChange={(e) => setNewDate(e.target.value)}
                                required
                            />
                            <div className="markdown-container">
                                <div className="markdown-header">
                                    <span>Markdown supported</span>
                                    <button 
                                        type="button"
                                        className="preview-btn"
                                        onClick={() => setIsPreview(!isPreview)}
                                    >
                                        {isPreview ? 'Edit' : 'Preview'}
                                    </button>
                                </div>
                                {isPreview ? (
                                    <div className="markdown-preview">
                                        <ReactMarkdown>{newUpdate}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <>
                                        <textarea
                                            placeholder="What's new... (Markdown supported)"
                                            value={newUpdate}
                                            onChange={(e) => setNewUpdate(e.target.value)}
                                            required
                                        />
                                        <div className="markdown-guide">
                                            <ReactMarkdown>{markdownGuide}</ReactMarkdown>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="form-buttons">
                                <button type="submit">Save</button>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsAdding(false);
                                        setIsPreview(false);
                                    }}
                                    className="cancel-btn"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            <div className="updates-table">
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>What's New</th>
                            {isAdmin && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {updates.map((update) => (
                            <tr key={update.id}>
                                <td className="date-cell">
                                    {editingId === update.id ? (
                                        <input
                                            type="text"
                                            value={editDate}
                                            onChange={(e) => setEditDate(e.target.value)}
                                            className="edit-input"
                                            required
                                        />
                                    ) : (
                                        update.date
                                    )}
                                </td>
                                <td className="update-cell">
                                    {editingId === update.id ? (
                                        <div className="edit-update-container">
                                            <div className="markdown-header">
                                                <span>Markdown supported</span>
                                                <button 
                                                    type="button"
                                                    className="preview-btn"
                                                    onClick={() => setIsEditPreview(!isEditPreview)}
                                                >
                                                    {isEditPreview ? 'Edit' : 'Preview'}
                                                </button>
                                            </div>
                                            {isEditPreview ? (
                                                <div className="markdown-preview">
                                                    <ReactMarkdown>{editUpdate}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <textarea
                                                    value={editUpdate}
                                                    onChange={(e) => setEditUpdate(e.target.value)}
                                                    className="edit-textarea"
                                                    required
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <ReactMarkdown components={linkRenderer}>{update.update}</ReactMarkdown>
                                    )}
                                </td>
                                {isAdmin && (
                                    <td className="actions-cell">
                                        {editingId === update.id ? (
                                            <div className="edit-actions">
                                                <button 
                                                    onClick={handleUpdate}
                                                    className="save-btn"
                                                >
                                                    Save
                                                </button>
                                                <button 
                                                    onClick={cancelEdit}
                                                    className="cancel-btn"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="row-actions">
                                                <button 
                                                    onClick={() => handleEdit(update)}
                                                    className="edit-btn"
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(update.id)}
                                                    className="delete-btn"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WhatIsNew; 